import React, { useMemo, useState, useEffect, useRef } from 'react';
import { toLocalYMD } from '../lib/dates';
import { 
  FileText, Search, Plus, Download, 
  Check, Clock, AlertTriangle, Send,
  Mail, Printer, Eye, EyeOff, Menu, Filter,
  PoundSterling, TrendingUp, Receipt, Loader2, RefreshCw,
  MessageSquare, ChevronDown, MailCheck, MailOpen, MailX, MousePointerClick
} from 'lucide-react';
import * as api from '../services/api';
import { safeOpenUrl } from '../utils/navigation';
import { toast } from 'sonner';

const statusConfig = {
  paid: { bg: 'bg-emerald-50', text: 'text-emerald-700', icon: Check, border: 'border-l-emerald-500' },
  pending: { bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock, border: 'border-l-amber-500' },
  overdue: { bg: 'bg-red-50', text: 'text-red-700', icon: AlertTriangle, border: 'border-l-red-500' },
  draft: { bg: 'bg-zinc-100', text: 'text-zinc-600', icon: FileText, border: 'border-l-zinc-400' }
};

const StatusBadge = ({ status }) => {
  const config = statusConfig[status] || statusConfig['pending'];
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold ${config.bg} ${config.text}`}>
      <Icon size={12} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

/**
 * Email tracking badge — shows the latest known Resend event for the
 * invoice's most recent send. Populated by:
 *   - `/api/webhook/resend` (live events from Resend's webhook)
 *   - The "Sync" action which polls /api/invoices/{id}/sync-email-status
 *
 * States (best → worst):
 *   clicked  → customer tapped "View & Pay"
 *   opened   → customer opened the email
 *   delivered→ inbox received it
 *   sent     → handed off to Resend, no further events yet
 *   bounced  → email bounced (bad address)
 *   complained → marked as spam
 */
const emailStatusConfig = {
  clicked:    { bg: 'bg-emerald-50',  text: 'text-emerald-700',  icon: MousePointerClick, label: 'Clicked Pay link' },
  opened:     { bg: 'bg-blue-50',     text: 'text-blue-700',     icon: MailOpen,          label: 'Opened' },
  delivered:  { bg: 'bg-sky-50',      text: 'text-sky-700',      icon: MailCheck,         label: 'Delivered' },
  sent:       { bg: 'bg-zinc-100',    text: 'text-zinc-600',     icon: Send,              label: 'Sent' },
  bounced:    { bg: 'bg-red-50',      text: 'text-red-700',      icon: MailX,             label: 'Bounced' },
  complained: { bg: 'bg-orange-50',   text: 'text-orange-700',   icon: AlertTriangle,     label: 'Marked spam' },
  unread:     { bg: 'bg-zinc-50',     text: 'text-zinc-500',     icon: EyeOff,            label: 'No email sent' },
};

const EmailStatusBadge = ({ invoice, onSync, syncing }) => {
  const status = invoice.emailStatus || (invoice.emailId ? 'sent' : 'unread');
  const config = emailStatusConfig[status] || emailStatusConfig['sent'];
  const Icon = config.icon;
  const ts = invoice.emailClickedAt || invoice.emailOpenedAt || invoice.emailDeliveredAt;
  const tsLabel = ts ? new Date(ts).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium ${config.bg} ${config.text}`}
      data-testid={`email-status-badge-${invoice.id}`}
      title={tsLabel ? `${config.label} · ${tsLabel}` : config.label}
    >
      <Icon size={11} />
      <span>{config.label}{tsLabel ? ` · ${tsLabel}` : ''}</span>
      {invoice.emailId && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSync && onSync(invoice); }}
          disabled={syncing}
          className="ml-1 -mr-0.5 p-0.5 rounded hover:bg-white/60 disabled:opacity-50"
          data-testid={`email-status-sync-${invoice.id}`}
          aria-label="Refresh email status"
        >
          <RefreshCw size={10} className={syncing ? 'animate-spin' : ''} />
        </button>
      )}
    </span>
  );
};

const Invoices = ({ onMenuClick }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewInvoiceForm, setShowNewInvoiceForm] = useState(false);
  const [sendingInvoiceId, setSendingInvoiceId] = useState(null);
  const [resendDropdownOpen, setResendDropdownOpen] = useState(null);
  const dropdownRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setResendDropdownOpen(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Resend invoice via email, SMS, or both
  const handleResendInvoice = async (invoice, method, e) => {
    e.stopPropagation();
    setResendDropdownOpen(null);
    setSendingInvoiceId(invoice.id);
    
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(`${API_URL}/api/invoices/${invoice.id}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ method })
      });
      
      const data = await response.json();
      if (response.ok) {
        if (data.status === 'success') {
          toast.success(`Invoice resent via ${method === 'both' ? 'email & SMS' : method}!`);
        } else if (data.status === 'partial') {
          toast.warning('Invoice partially sent - check results');
        } else if (data.status === 'simulated') {
          toast.info('Email service not configured - simulated send');
        } else {
          toast.error(data.results?.email?.error || data.results?.sms?.error || 'Failed to resend');
        }
      } else {
        toast.error(data.detail || 'Failed to resend invoice');
      }
    } catch (err) {
      console.error('Failed to resend invoice:', err);
      toast.error('Failed to resend invoice');
    } finally {
      setSendingInvoiceId(null);
    }
  };

  // Send invoice via email (legacy function for backwards compatibility)
  const handleSendInvoice = async (invoice, e) => {
    handleResendInvoice(invoice, 'email', e);
  };

  // Manually sync an invoice's email delivery status from Resend.
  // Used when the customer's invoice was sent before the webhook was
  // configured, or when an event was missed.
  const [syncingInvoiceId, setSyncingInvoiceId] = useState(null);
  const handleSyncEmailStatus = async (invoice) => {
    if (!invoice.rawId) return;
    setSyncingInvoiceId(invoice.id);
    try {
      const token = localStorage.getItem('streakfree_token');
      const response = await fetch(
        `${API_URL}/api/invoices/${invoice.rawId}/sync-email-status`,
        { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } }
      );
      const data = await response.json();
      if (response.ok) {
        toast.success(`Status: ${data.last_event}`);
        // Refresh the invoice list so the new badge state shows
        await fetchInvoices();
      } else {
        toast.error(data.detail || 'Failed to sync');
      }
    } catch (err) {
      console.error('Sync email status failed:', err);
      toast.error('Failed to sync email status');
    } finally {
      setSyncingInvoiceId(null);
    }
  };

  // View invoice in new tab
  const handleViewInvoice = (invoice, e) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    safeOpenUrl(`${baseUrl}/invoice/${invoice.id}`);
  };

  // Print invoice
  const handlePrintInvoice = (invoice, e) => {
    e.stopPropagation();
    const baseUrl = window.location.origin;
    // For print we still need window.open to get a reference to the window
    const link = document.createElement('a');
    link.href = `${baseUrl}/invoice/${invoice.id}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    // Note: Print functionality will need the user to manually print from the new tab
    // as we can't get a reference to the window using the anchor approach
  };

  // Fetch invoices from API
  const fetchInvoices = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.getInvoices({ limit: 100 });
      // Transform API data to component format
      const transformed = data.map((inv) => ({
        id: inv.invoice_number || inv.id,
        rawId: inv.id,
        customerId: inv.customer_id,
        customerName: inv.customer_name || 'Unknown Customer',
        customerEmail: inv.customer_email || '',
        customerPhone: inv.customer_phone || '',
        customerAddress: inv.customer_address || '',
        amount: inv.total || 0,
        status: inv.status || 'pending',
        invoiceDate: inv.created_at?.split('T')[0] || toLocalYMD(new Date()),
        dueDate: inv.due_date || '',
        description: inv.items?.[0]?.description || 'Service',
        items: inv.items || [],
        // Email delivery tracking (populated by Resend webhook + manual sync)
        emailId: inv.email_id || null,
        emailStatus: inv.email_status || null,            // sent / delivered / opened / clicked / bounced / complained
        emailDeliveredAt: inv.email_delivered_at || null,
        emailOpenedAt: inv.email_opened_at || null,
        emailClickedAt: inv.email_clicked_at || null,
        emailBouncedAt: inv.email_bounced_at || null,
      }));
      setInvoices(transformed);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices();
    
    // Check if we should open create form
    const openCreate = sessionStorage.getItem('openCreateForm');
    if (openCreate === 'true') {
      setShowNewInvoiceForm(true);
      sessionStorage.removeItem('openCreateForm');
    }
  }, []);

  // "+ New → New Invoice" from the global header — opens the form even if we're
  // already on this page.
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.action !== 'invoices') return;
      sessionStorage.removeItem('openCreateForm');
      setShowNewInvoiceForm(true);
    };
    window.addEventListener('streakfree:open-create-form', handler);
    return () => window.removeEventListener('streakfree:open-create-form', handler);
  }, []);

  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      const matchesSearch = inv.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           inv.id.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || inv.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [invoices, searchTerm, statusFilter]);

  const stats = useMemo(() => {
    const total = invoices.reduce((sum, inv) => sum + inv.amount, 0);
    const paid = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.amount, 0);
    const pending = invoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + inv.amount, 0);
    const overdue = invoices.filter(inv => inv.status === 'overdue').reduce((sum, inv) => sum + inv.amount, 0);
    return { total, paid, pending, overdue };
  }, [invoices]);

  const statusFilters = [
    { id: 'all', label: 'All', count: invoices.length },
    { id: 'paid', label: 'Paid', count: invoices.filter(i => i.status === 'paid').length },
    { id: 'pending', label: 'Pending', count: invoices.filter(i => i.status === 'pending').length },
    { id: 'overdue', label: 'Overdue', count: invoices.filter(i => i.status === 'overdue').length },
    { id: 'draft', label: 'Draft', count: invoices.filter(i => i.status === 'draft').length },
  ];

  return (
    <div className="h-full flex flex-col bg-zinc-100" data-testid="invoices-page">
      {/* Modern Header */}
      <div className="bg-zinc-900 text-white sticky top-0 z-30" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        {/* Top bar */}
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <button 
              onClick={onMenuClick}
              className="p-2 -ml-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
            <h1 className="text-xl font-bold tracking-tight">Invoices</h1>
          </div>
          <button 
            onClick={() => setShowNewInvoiceForm(true)}
            className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 px-4 py-2.5 rounded-xl transition-colors font-medium text-sm"
            data-testid="create-invoice-btn"
          >
            <Plus size={18} />
            New Invoice
          </button>
        </div>

        {/* Stats Cards */}
        <div className="px-4 pb-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <Receipt size={14} className="text-white/70" />
                <p className="text-white/70 text-xs font-medium">Total</p>
              </div>
              <p className="text-xl font-bold">£{stats.total.toFixed(2)}</p>
            </div>
            <div className="bg-emerald-500/20 backdrop-blur-sm rounded-xl p-3 border border-emerald-400/30">
              <div className="flex items-center gap-2 mb-1">
                <Check size={14} className="text-emerald-300" />
                <p className="text-emerald-300 text-xs font-medium">Paid</p>
              </div>
              <p className="text-xl font-bold text-emerald-300">£{stats.paid.toFixed(2)}</p>
            </div>
            <div className="bg-amber-500/20 backdrop-blur-sm rounded-xl p-3 border border-amber-400/30">
              <div className="flex items-center gap-2 mb-1">
                <Clock size={14} className="text-amber-300" />
                <p className="text-amber-300 text-xs font-medium">Pending</p>
              </div>
              <p className="text-xl font-bold text-amber-300">£{stats.pending.toFixed(2)}</p>
            </div>
            <div className="bg-red-500/20 backdrop-blur-sm rounded-xl p-3 border border-red-400/30">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={14} className="text-red-300" />
                <p className="text-red-300 text-xs font-medium">Overdue</p>
              </div>
              <p className="text-xl font-bold text-red-300">£{stats.overdue.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="p-4 bg-white border-b border-zinc-200">
        <div className="relative mb-3">
          <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            placeholder="Search invoices..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-zinc-50 border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
            data-testid="invoice-search"
          />
        </div>
        
        {/* Status Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 no-scrollbar">
          {statusFilters.map(filter => (
            <button
              key={filter.id}
              onClick={() => setStatusFilter(filter.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                statusFilter === filter.id 
                  ? 'bg-zinc-900 text-white shadow-sm' 
                  : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
              }`}
              data-testid={`filter-${filter.id}`}
            >
              {filter.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-md ${
                statusFilter === filter.id ? 'bg-white/20' : 'bg-zinc-200'
              }`}>
                {filter.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Invoice List */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-3">
          {filteredInvoices.map(invoice => {
            const config = statusConfig[invoice.status] || statusConfig['pending'];
            return (
              <div 
                key={invoice.id}
                onClick={() => setSelectedInvoice(invoice)}
                className={`bg-white rounded-xl border-l-4 shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden ${config.border}`}
                data-testid={`invoice-${invoice.id}`}
              >
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 bg-zinc-100 rounded-xl flex items-center justify-center">
                        <FileText size={20} className="text-zinc-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-zinc-900">{invoice.id}</p>
                          <StatusBadge status={invoice.status} />
                          <EmailStatusBadge
                            invoice={invoice}
                            onSync={handleSyncEmailStatus}
                            syncing={syncingInvoiceId === invoice.id}
                          />
                        </div>
                        <p className="text-sm text-zinc-500 mt-0.5">{invoice.customerName}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-zinc-900">£{invoice.amount.toFixed(2)}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">Due: {invoice.dueDate}</p>
                    </div>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-zinc-100">
                    <button 
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
                      onClick={(e) => handleViewInvoice(invoice, e)}
                      data-testid={`view-invoice-${invoice.id}`}
                    >
                      <Eye size={14} />
                      View
                    </button>
                    
                    {/* Resend Dropdown */}
                    <div className="relative" ref={resendDropdownOpen === invoice.id ? dropdownRef : null}>
                      <button 
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                        onClick={(e) => {
                          e.stopPropagation();
                          setResendDropdownOpen(resendDropdownOpen === invoice.id ? null : invoice.id);
                        }}
                        disabled={sendingInvoiceId === invoice.id}
                        data-testid={`resend-invoice-${invoice.id}`}
                      >
                        {sendingInvoiceId === invoice.id ? (
                          <Loader2 size={14} className="animate-spin" />
                        ) : (
                          <Send size={14} />
                        )}
                        {sendingInvoiceId === invoice.id ? 'Sending...' : 'Send'}
                        <ChevronDown size={12} className={`transition-transform ${resendDropdownOpen === invoice.id ? 'rotate-180' : ''}`} />
                      </button>
                      
                      {resendDropdownOpen === invoice.id && (
                        <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-zinc-200 py-1 min-w-[140px] z-50">
                          <button
                            onClick={(e) => handleResendInvoice(invoice, 'email', e)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                            disabled={!invoice.customerEmail}
                            data-testid={`resend-email-${invoice.id}`}
                          >
                            <Mail size={14} className="text-blue-500" />
                            Email
                            {!invoice.customerEmail && <span className="text-xs text-zinc-400 ml-auto">N/A</span>}
                          </button>
                          <button
                            onClick={(e) => handleResendInvoice(invoice, 'sms', e)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50"
                            disabled={!invoice.customerPhone}
                            data-testid={`resend-sms-${invoice.id}`}
                          >
                            <MessageSquare size={14} className="text-emerald-500" />
                            SMS
                            {!invoice.customerPhone && <span className="text-xs text-zinc-400 ml-auto">N/A</span>}
                          </button>
                          <button
                            onClick={(e) => handleResendInvoice(invoice, 'both', e)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 border-t border-zinc-100"
                            disabled={!invoice.customerEmail && !invoice.customerPhone}
                            data-testid={`resend-both-${invoice.id}`}
                          >
                            <Send size={14} className="text-purple-500" />
                            Email & SMS
                          </button>
                        </div>
                      )}
                    </div>
                    
                    <button 
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
                      onClick={(e) => handleViewInvoice(invoice, e)}
                    >
                      <Download size={14} />
                      Download
                    </button>
                    <button 
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-100 rounded-lg transition-colors"
                      onClick={(e) => handlePrintInvoice(invoice, e)}
                    >
                      <Printer size={14} />
                      Print
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {filteredInvoices.length === 0 && (
          <div className="flex flex-col items-center justify-center h-64 text-center px-8">
            <div className="w-16 h-16 rounded-2xl bg-zinc-200 flex items-center justify-center mb-4">
              <FileText size={28} className="text-zinc-400" />
            </div>
            <h3 className="text-lg font-semibold text-zinc-900 mb-2">No invoices found</h3>
            <p className="text-zinc-500">Try adjusting your search or filter</p>
          </div>
        )}
      </div>

      {/* FAB */}
      <button 
        onClick={() => setShowNewInvoiceForm(true)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 rounded-2xl shadow-lg shadow-emerald-500/30 flex items-center justify-center transition-all hover:scale-105 z-50"
        data-testid="fab-new-invoice"
      >
        <Plus size={24} className="text-white" />
      </button>
      
      {/* New Invoice Modal - Placeholder */}
      {showNewInvoiceForm && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowNewInvoiceForm(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-zinc-900">Create New Invoice</h3>
              <button onClick={() => setShowNewInvoiceForm(false)} className="p-2 hover:bg-zinc-100 rounded-lg">
                <Plus size={20} className="text-zinc-500 rotate-45" />
              </button>
            </div>
            <div className="p-5 text-center">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <FileText size={28} className="text-emerald-600" />
              </div>
              <p className="text-zinc-600 mb-4">
                To create an invoice, go to a customer's profile and click "Create Invoice" from their details page.
              </p>
              <button
                onClick={() => setShowNewInvoiceForm(false)}
                className="px-6 py-3 bg-emerald-500 rounded-xl text-white font-medium hover:bg-emerald-600"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;
